import React, { useEffect, useRef, type CSSProperties, type ReactNode } from 'react';

import { EyeOutlined, CodeOutlined } from '@ant-design/icons';
import useLiquidRenderer from '@hooks/shared/useLiquidRenderer';
import { Tabs, Card, Spin, Alert } from 'antd';

import TemplateIframe from './TemplateIframe';

interface PaperSize {
  // 部分 caller (ProductLabelTemplatesShow) 在 template 尚未載入時會傳入 undefined；
  // wrapper 內部會以 defaults 處理，故宣告 optional 以放行
  width?: number;
  height?: number;
}

interface TemplateDetailTabsProps {
  content?: string;
  sampleData?: Record<string, unknown>; // 🔥 外部輸入 data 的入口
  paperSize: PaperSize;
  cssVariablePrefix?: string;
  t: (key: string, options?: Record<string, unknown>) => string;
  namespace?: string;
  modelName?: string;
  iframeStyle?: CSSProperties;
}

/**
 * TemplateDetailTabs - Reusable tabs component for template detail view (Preview + Source)
 *
 * @param props.content - Liquid template content
 * @param props.sampleData - Sample data for rendering (EXTERNAL INPUT - 外部輸入 data 的入口)
 * @param props.paperSize - Paper size { width, height } in mm
 * @param props.cssVariablePrefix - CSS variable prefix ('paper' or 'label')
 * @param props.t - i18n translate function
 * @param props.namespace - i18n namespace (e.g., 'shared.order_templates')
 * @param props.modelName - Model name for i18n (e.g., 'order_template', 'product_label_template')
 * @param props.iframeStyle - Additional styles for iframe
 */
const TemplateDetailTabs: React.FC<TemplateDetailTabsProps> = ({
  content,
  sampleData, // 🔥 外部輸入 data 的入口
  paperSize,
  cssVariablePrefix = 'paper',
  t,
  namespace = 'shared.order_templates',
  modelName = 'order_template',
  iframeStyle = {},
}) => {
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const useLiquidRendererTyped = useLiquidRenderer as unknown as (args: { sampleData?: Record<string, unknown> }) => {
    render: (content: string, sampleData: Record<string, unknown>) => void;
    rendering: boolean;
    error: ReactNode;
    html: string;
  };
  const { render, rendering, error, html } = useLiquidRendererTyped({ sampleData });

  // Render template on mount and when dependencies change
  useEffect(() => {
    if (content && sampleData) {
      render(content, sampleData);
    }
  }, [content, sampleData, render]);

  // Update iframe CSS variables when paperSize changes
  useEffect(() => {
    if (iframeRef.current) {
      try {
        const iframeDoc = iframeRef.current.contentDocument ||
                         iframeRef.current.contentWindow!.document;
        if (iframeDoc?.body) {
          iframeDoc.body.style.setProperty(`--${cssVariablePrefix}-width`, `${paperSize.width}mm`);
          iframeDoc.body.style.setProperty(`--${cssVariablePrefix}-height`, `${paperSize.height}mm`);

          // Update iframe height
          const contentHeight = iframeDoc.body.scrollHeight;
          iframeRef.current.style.height = `${contentHeight + 20}px`;
        }
      } catch (err) {
        console.error('Failed to update iframe:', err);
      }
    }
  }, [paperSize, cssVariablePrefix]);

  const tabItems = [
    {
      key: 'preview',
      label: (
        <span>
          <EyeOutlined style={{ marginRight: 6 }} />
          {t(`${namespace}.detail.preview_tab`)}
        </span>
      ),
      children: (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'flex-start',
            padding: '20px',
            backgroundColor: '#f5f5f5',
            minHeight: '800px'
          }}
        >
          {rendering ? (
            <div style={{ textAlign: 'center', padding: '100px' }}>
              <Spin size="large" tip={t(`${namespace}.detail.rendering`)} />
            </div>
          ) : error ? (
            <Alert
              message={t(`${namespace}.detail.render_error`)}
              description={error}
              type="error"
              showIcon
              style={{ maxWidth: '600px', margin: '20px' }}
            />
          ) : (
            <TemplateIframe
              ref={iframeRef}
              html={html}
              paperSize={paperSize}
              cssVariablePrefix={cssVariablePrefix}
              style={iframeStyle}
            />
          )}
        </div>
      )
    },
    {
      key: 'source',
      label: (
        <span>
          <CodeOutlined style={{ marginRight: 6 }} />
          {t(`${namespace}.detail.source_tab`)}
        </span>
      ),
      children: (
        <Card
          title={t(`models.${modelName}.content`)}
          size="small"
          type="inner"
        >
          <pre
            style={{
              backgroundColor: '#f5f5f5',
              padding: '16px',
              borderRadius: '4px',
              overflow: 'auto',
              maxHeight: '600px',
              fontSize: '13px',
              lineHeight: '1.6',
              margin: 0,
              fontFamily: 'Monaco, Menlo, Consolas, "Courier New", monospace'
            }}
          >
            {content || '-'}
          </pre>
        </Card>
      )
    }
  ];

  return (
    <div style={{ marginTop: '24px' }}>
      <Alert
        message={t(`${namespace}.detail.content_hint`)}
        description={t(`${namespace}.detail.content_description`)}
        type="info"
        showIcon
        style={{ marginBottom: '16px' }}
      />

      <Tabs defaultActiveKey="preview" items={tabItems} />
    </div>
  );
};

export default TemplateDetailTabs;
